<template>
    <h1
        class="fs-3"
        v-if="editMode">
        {{ form.oriName }} ({{ props.id }})
    </h1>
    <h1
        class="fs-3"
        v-else>
        New Agency
    </h1>
    <div class="container pt-3">
        <ul
            class="nav nav-tabs"
            id="myTab"
            role="tablist">
            <li
                v-for="tab in tabs"
                class="nav-item"
                role="presentation"
                :key="tab.id">
                <button
                    class="nav-link"
                    :class="{active: tab.id == selectedTab}"
                    data-bs-toggle="tab"
                    :data-bs-target="`#${tab.id}`"
                    type="button"
                    role="tab"
                    :id="`tab-${tab.id}`"
                    @click.prevent="changeTab(tab.id)"
                    :aria-controls="tab.id"
                    :aria-selected="tab.id == selectedTab">
                    {{ tab.name }}
                    <span
                        v-if="tabHasError(tab.id)"
                        class="text-danger ms-1"
                        aria-label="This tab has validation errors"
                        >●</span
                    >
                </button>
            </li>
        </ul>

        <div
            class="tab-content"
            v-if="settings">
            <div
                class="tab-pane fade"
                :class="{'show active': selectedTab === 'ori'}"
                id="ori"
                role="tabpanel"
                aria-labelledby="tab-ori"
                tabindex="0">
                <div class="container-fluid py-4">
                    <div class="row mb-3">
                        <div class="col-md-4">
                            <label
                                for="ori9"
                                class="form-label">
                                Agency ORI
                            </label>
                            <b-input
                                name="ori9"
                                v-model="form.ori"
                                :max-length="9"
                                :disabled="editMode"
                                :error="errors.ori" />
                        </div>
                        <div class="col-md-4">
                            <label
                                for="agencyName"
                                class="form-label"
                                >Agency Name</label
                            >
                            <b-input
                                name="agencyName"
                                v-model="form.oriName"
                                :error="errors.oriName" />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-4">
                            <label
                                for="agencyType"
                                class="form-label">
                                Agency Type
                            </label>
                            <b-select
                                name="agencyType"
                                v-model="form.agencyTypeCode"
                                :items="agencyTypes"
                                :error="errors.agencyTypeCode" />
                        </div>
                        <div class="col-md-4">
                            <label
                                for="county"
                                class="form-label">
                                {{ settings.county.text || "County" }}
                            </label>
                            <b-select
                                name="county"
                                v-model="form.countyCode"
                                :items="counties"
                                :error="errors.countyCode" />
                        </div>
                    </div>

                    <div
                        class="row mb-4"
                        v-if="settings?.latLong.visible">
                        <div class="col-md-4">
                            <label
                                class="form-label"
                                for="lat">
                                Latitude
                            </label>
                            <b-input
                                v-model="form.latitude"
                                type="number"
                                name="lat"
                                :disabled="!settings.latLong.enabled"
                                :error="errors.latitude" />
                        </div>
                        <div class="col-md-4">
                            <label
                                class="form-label"
                                for="long">
                                Longitude
                            </label>
                            <b-input
                                v-model="form.longitude"
                                type="number"
                                name="long"
                                :disabled="!settings.latLong.enabled"
                                :error="errors.longitude" />
                        </div>
                    </div>

                    <b-card
                        header="Submitting ORI"
                        v-if="settings?.ori7.visible">
                        <div class="row">
                            <div class="col-md-4">
                                <label
                                    class="form-label"
                                    for="ori7">
                                    ORI used for Summary Submissions
                                </label>
                                <b-input
                                    name="ori7"
                                    v-model="form.ori7"
                                    :max-length="7"
                                    :disabled="!settings?.ori7.enabled"
                                    :error="errors.ori7" />
                            </div>

                            <div class="col-md-4">
                                <label
                                    class="form-label"
                                    for="ori7Fbi">
                                    ORI used for FBI Summary Submissions
                                </label>

                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="sync7"
                                        :disabled="!settings?.ori7Fbi.enabled"
                                        v-model="flags.sameAs7" />
                                    <label
                                        class="form-check-label small"
                                        for="sync7"
                                        >Same as 7-Digit ORI</label
                                    >
                                </div>
                                <b-select
                                    name="ori7Fbi"
                                    v-model="form.ori7Fbi"
                                    :items="ori7s"
                                    :disabled="flags.sameAs7"
                                    :error="errors.ori7Fbi" />
                            </div>
                        </div>
                    </b-card>

                    <b-card
                        header="ORI for FBI Submission"
                        v-if="settings?.ori9Fbi.visible">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="toFbi"
                                        :checked="form.toFbi"
                                        disabled />
                                    <label
                                        class="form-check-label small"
                                        for="toFbi"
                                        >Data included in federal submissions</label
                                    >
                                </div>

                                <div
                                    class="form-check"
                                    v-if="form.toFbi">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        id="differentOri9"
                                        :checked="flags.differentOri9"
                                        disabled />
                                    <label
                                        class="form-check-label small"
                                        for="differentOri9"
                                        >Use different ORI for federal submissions</label
                                    >
                                </div>

                                <b-select
                                    v-if="form.toFbi && flags.differentOri9"
                                    name="ori9Fbi"
                                    aria-label="Federal submission ORI"
                                    v-model="form.ori9Fbi"
                                    :items="ori9s"
                                    :disabled="true"
                                    :error="errors.ori9Fbi" />
                            </div>
                        </div>
                    </b-card>

                    <b-card
                        :header="notesText"
                        v-if="settings?.notes.visible && canViewNotes && editMode">
                        <div class="row">
                            <div class="col-md-12">
                                <label
                                    class="visiblity-hidden"
                                    for="note"
                                    >{{ notesText }}</label
                                >
                                <b-textarea
                                    name="note"
                                    v-model="form.itemNote"
                                    :disabled="!canManageNotes || !settings?.notes.enabled"></b-textarea>

                                <b-button
                                    v-if="canManageNotes && settings?.notes.enabled"
                                    variant="primary"
                                    @click.prevent="updateNotes"
                                    class="mt-2"
                                    >Save Notes</b-button
                                >
                            </div>
                        </div>
                    </b-card>
                </div>
            </div>
            <div
                class="tab-pane fade"
                :class="{'show active': selectedTab === 'data_collections'}"
                id="data_collections"
                role="tabpanel"
                aria-labelledby="tab-data_collections"
                tabindex="0">
                <div class="py-4">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th scope="col">Name</th>
                                <th scope="col">Submitting</th>
                                <th scope="col">Start Date</th>
                                <th scope="col">End Date</th>
                                <th scope="col">Vendor</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(dataCollection, index) in form.dataCollectionStatus"
                                :key="dataCollection.dataSetId">
                                <th scope="row">{{ dataCollection.label }}</th>
                                <td>
                                    <input
                                        type="checkbox"
                                        class="form-check-input"
                                        :disabled="true"
                                        :checked="getDataCollectionStatus(dataCollection)"
                                        :aria-label="`${dataCollection.label} submitting`" />
                                </td>
                                <td>
                                    <b-date-picker
                                        :id="`dcStartDate-${dataCollection.dataSetId}`"
                                        :aria-label="`${dataCollection.label} start date`"
                                        v-model="dataCollection.agencyStartDate"></b-date-picker>
                                </td>
                                <td>
                                    <b-date-picker
                                        :id="`dcEndDate-${dataCollection.dataSetId}`"
                                        :aria-label="`${dataCollection.label} end date`"
                                        :disabled="dataCollection.startDate == null"
                                        :error="errors[`dataCollectionStatus[${index}].agencyEndDate`]"
                                        :error-id="`dcEndDate-${dataCollection.dataSetId}-error`"
                                        v-model="dataCollection.agencyEndDate"></b-date-picker>
                                </td>
                                <td>
                                    <b-select
                                        :name="`dcVendor-${dataCollection.dataSetId}`"
                                        :aria-label="`${dataCollection.label} vendor`"
                                        :items="vendors"
                                        select-text="Select vendor"
                                        :error="errors[`dataCollectionStatus[${index}].vendorCode`]"
                                        v-model="dataCollection.vendorCode"></b-select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div
                class="tab-pane fade"
                :class="{'show active': selectedTab === 'addresses'}"
                id="addresses"
                role="tabpanel"
                aria-labelledby="tab-addresses"
                tabindex="0">
                <div class="mt-4">
                    <b-card
                        :header="settings?.mailingAddress.text || 'Mailing Address'"
                        v-if="settings?.mailingAddress.visible">
                        <div class="row">
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="mailingAddressStreet"
                                    >Street</label
                                >
                                <b-input
                                    v-model="form.mailStreet"
                                    name="mailingAddressStreet"
                                    :error="errors.mailStreet" />
                            </div>
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="mailingAddressCity"
                                    >City</label
                                >
                                <b-input
                                    v-model="form.mailTown"
                                    name="mailingAddressCity"
                                    :error="errors.mailTown" />
                            </div>
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="mailingAddressState"
                                    >State</label
                                >
                                <b-input
                                    v-model="form.mailState"
                                    name="mailingAddressState"
                                    :error="errors.mailState" />
                            </div>
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="mailingAddressZip"
                                    >ZIP</label
                                >
                                <b-input
                                    v-model="form.mailZip"
                                    name="mailingAddressZip"
                                    :error="errors.mailZip" />
                            </div>
                        </div>
                    </b-card>

                    <b-card
                        :header="settings?.locationAddress.text || 'Location Address'"
                        v-if="settings?.locationAddress.visible">
                        <div class="row">
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="locationAddressStreet"
                                    >Street</label
                                >
                                <b-input
                                    v-model="form.locStreet"
                                    name="locationAddressStreet"
                                    :error="errors.locStreet" />
                            </div>
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="locationAddressCity"
                                    >City</label
                                >
                                <b-input
                                    v-model="form.locTown"
                                    name="locationAddressCity"
                                    :error="errors.locTown" />
                            </div>
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="locationAddressState"
                                    >State</label
                                >
                                <b-input
                                    v-model="form.locState"
                                    name="locationAddressState"
                                    :error="errors.locState" />
                            </div>
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="locationAddressZip"
                                    >ZIP</label
                                >
                                <b-input
                                    v-model="form.locZip"
                                    name="locationAddressZip"
                                    :error="errors.locZip" />
                            </div>
                        </div>
                    </b-card>
                </div>
            </div>

            <div
                id="contacts"
                class="tab-pane fade"
                :class="{'show active': selectedTab === 'contacts'}"
                role="tabpanel"
                aria-labelledby="tab-contacts"
                tabindex="0">
                <div class="mt-4">
                    <b-card
                        :header="contactsAgencyAdministratorText"
                        v-if="settings?.contactsAgencyAdministrator.visible">
                        <div class="row">
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsAgencyAdministratorName"
                                    >Name</label
                                >
                                <b-input
                                    id="contactsAgencyAdministratorName"
                                    name="contactsAgencyAdministratorName"
                                    v-model="form.adminName"
                                    :error="errors.adminName" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsAgencyAdministratorEmail"
                                    >Email</label
                                >
                                <b-input
                                    id="contactsAgencyAdministratorEmail"
                                    name="contactsAgencyAdministratorEmail"
                                    v-model="form.adminEmail"
                                    :error="errors.adminEmail" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsAgencyAdministratorPhone"
                                    >Phone</label
                                >
                                <b-input
                                    id="contactsAgencyAdministratorPhone"
                                    name="contactsAgencyAdministratorPhone"
                                    v-model="form.adminPhone"
                                    :error="errors.adminPhone" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsAgencyAdministratorFax"
                                    >Fax</label
                                >
                                <b-input
                                    id="contactsAgencyAdministratorFax"
                                    name="contactsAgencyAdministratorFax"
                                    v-model="form.adminFax"
                                    :error="errors.adminFax" />
                            </div>
                        </div>
                    </b-card>

                    <b-card
                        :header="contactsUcrProgramManagerText"
                        v-if="settings?.contactsUcrProgramManager.visible">
                        <div class="row">
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsUcrProgramManagerName"
                                    >Name</label
                                >
                                <b-input
                                    id="contactsUcrProgramManagerName"
                                    name="contactsUcrProgramManagerName"
                                    v-model="form.ucrName"
                                    :error="errors.ucrName" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsUcrProgramManagerEmail"
                                    >Email</label
                                >
                                <b-input
                                    id="contactsUcrProgramManagerEmail"
                                    name="contactsUcrProgramManagerEmail"
                                    v-model="form.ucrEmail"
                                    :error="errors.ucrEmail" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsUcrProgramManagerPhone"
                                    >Phone</label
                                >
                                <b-input
                                    id="contactsUcrProgramManagerPhone"
                                    name="contactsUcrProgramManagerPhone"
                                    v-model="form.ucrPhone"
                                    :error="errors.ucrPhone" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsUcrProgramManagerFax"
                                    >Fax</label
                                >
                                <b-input
                                    id="contactsUcrProgramManagerFax"
                                    name="contactsUcrProgramManagerFax"
                                    v-model="form.ucrFax"
                                    :error="errors.ucrFax" />
                            </div>
                        </div>
                    </b-card>
                    <b-card
                        :header="contactsCommanderText"
                        v-if="settings?.contactsCommander.visible">
                        <div class="row">
                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsCommanderName"
                                    >Name</label
                                >
                                <b-input
                                    id="contactsCommanderName"
                                    name="contactsCommanderName"
                                    v-model="form.commName"
                                    :error="errors.commName" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsCommanderEmail"
                                    >Email</label
                                >
                                <b-input
                                    id="contactsCommanderEmail"
                                    name="contactsCommanderEmail"
                                    v-model="form.commEmail"
                                    :error="errors.commEmail" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsCommanderPhone"
                                    >Phone</label
                                >
                                <b-input
                                    id="contactsCommanderPhone"
                                    name="contactsCommanderPhone"
                                    v-model="form.commPhone"
                                    :error="errors.commPhone" />
                            </div>

                            <div class="col-md-4 mb-2">
                                <label
                                    class="form-label"
                                    for="contactsCommanderFax"
                                    >Fax</label
                                >
                                <b-input
                                    id="contactsCommanderFax"
                                    name="contactsCommanderFax"
                                    v-model="form.commFax" />
                            </div>
                        </div>
                    </b-card>
                </div>
            </div>
        </div>
        <div class="d-flex justify-content-between mt-4 px-2">
            <div>
                <b-button
                    variant="secondary"
                    to="Agencies.aspx"
                    >Cancel</b-button
                >
            </div>
            <div>
                <b-button
                    variant="secondary"
                    class="me-2"
                    @click.prevent="prevTab"
                    v-if="currentIndex !== 0">
                    Previous
                </b-button>

                <b-button
                    variant="primary"
                    @click.prevent="nextTab"
                    v-if="currentIndex < tabs.length - 1">
                    Next
                </b-button>

                <b-button
                    variant="primary"
                    @click.prevent="save"
                    v-else>
                    Finish
                </b-button>
            </div>
        </div>
        <div v-if="editMode">Last Updated: {{ form.lastUpdate }}</div>
    </div>
    <system-alerts></system-alerts>
    <confirm-modal></confirm-modal>
</template>
<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {BCard, SystemAlerts, ConfirmModal} from "./Misc";
import {BInput, BDatePicker, BTextarea, BSelect} from "./Fields";
import type {AgencyItemDetails, DataCollectionStatus} from "@/types";
import {useFormValidation} from "@/validation";
import {BButton} from "./BButton";
import {storeToRefs} from "pinia";
import {useAgencyStore} from "@/stores/agencyStore";
import {useVendorStore} from "@/stores/vendorStore";
import hasPermission from "@/composable/permissionHelper";
import {useAgencyTypeStore} from "@/stores/agencyTypesStore";
import {useCountyStore} from "@/stores/countyStore";
import {array, mixed, object, string} from "yup";
const agencyTypeStore = useAgencyTypeStore();
const countyStore = useCountyStore();
const vendorStore = useVendorStore();

const props = defineProps({
    id: String,
});

const agencyTypes = computed(() => {
    return agencyTypeStore.items?.map((x) => ({value: x.code, text: x.label}));
});
const counties = computed(() => {
    return countyStore.items?.map((x) => ({value: x.code, text: x.name}));
});

const vendors = computed(() => {
    return vendorStore.items?.map((x) => ({value: x.code, text: x.name}));
});

const ori9s = computed(() => {
    return lookups.value?.map((x) => ({value: x.ori, text: x.ori}));
});
const ori7s = computed(() => {
    return lookups.value?.map((x) => ({value: x.ori7, text: x.ori7}));
});

const contactsAgencyAdministratorText = computed(() => {
    return settings.value?.contactsAgencyAdministrator.text || "Agency Administrator";
});

const contactsUcrProgramManagerText = computed(() => {
    return settings.value?.contactsUcrProgramManager.text || "UCR Program Manager";
});

const contactsCommanderText = computed(() => {
    return settings.value?.contactsCommander.text || "Commander";
});

const notesText = computed(() => {
    return settings.value?.notes.text || "Notes";
});

const editMode = computed(() => {
    return props.id != "";
});

// Normalizes a date value to "yyyy-MM-dd" so comparisons are by calendar day, not time/time zone.
const toDateKey = (value: string | Date | null | undefined) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
    }
    return value.substring(0, 10);
};

// Submitting = vendor assigned and today falls within the agency start/end dates (both inclusive).
const getDataCollectionStatus = (item: DataCollectionStatus) => {
    const today = toDateKey(new Date())!;
    const startDate = toDateKey(item.agencyStartDate);
    const endDate = toDateKey(item.agencyEndDate);

    return !!item.vendorCode && startDate != null && startDate <= today && (endDate == null || endDate >= today);
};

const flags = ref({sameAs7: false, differentOri9: false});
const selectedTab = ref("ori");

const allTabs = [
    {name: "Ori", id: "ori"},
    {name: "Data Collections", id: "data_collections"},
    {name: "Addresses", id: "addresses"},
    {name: "Contacts", id: "contacts"},
];


const tabs = computed(() => {
    return allTabs.filter((tab) => {
        if (tab.id === "addresses") {
            return settings.value?.mailingAddress.visible || settings.value?.locationAddress.visible;
        }

        if (tab.id === "contacts") {
            return (
                settings.value?.contactsAgencyAdministrator.visible ||
                settings.value?.contactsUcrProgramManager.visible ||
                settings.value?.contactsCommander.visible
            );
        }

        return true;
    });
});

const fieldTabMap: Record<string, string> = {
    oriName: "ori",
    agencyTypeCode: "ori",
    countyCode: "ori",
    latitude: "ori",
    longitude: "ori",
    ori: "ori",
    ori7: "ori",
    ori7Fbi: "ori",
    ori9Fbi: "ori",
    mailStreet: "addresses",
    mailTown: "addresses",
    mailState: "addresses",
    mailZip: "addresses",
    locStreet: "addresses",
    locTown: "addresses",
    locState: "addresses",
    locZip: "addresses",
    adminName: "contacts",
    adminEmail: "contacts",
    adminPhone: "contacts",
    adminFax: "contacts",
    ucrName: "contacts",
    ucrEmail: "contacts",
    ucrPhone: "contacts",
    ucrFax: "contacts",
    commName: "contacts",
    commEmail: "contacts",
    commPhone: "contacts",
    commFax: "contacts",
};

const getFieldTab = (field: string) => (field.startsWith("dataCollectionStatus") ? "data_collections" : fieldTabMap[field]);

const tabHasError = (tabId: string) => Object.keys(errors.value ?? {}).some((field) => getFieldTab(field) === tabId);

const agencyStore = useAgencyStore();

const {loading, settings, lookups, datasets} = storeToRefs(agencyStore);

const form = ref({} as AgencyItemDetails);

const currentIndex = computed(() => tabs.value.findIndex((t) => t.id === selectedTab.value));

const changeTab = async (tabId: string) => {
    if (await handleSubmit(schema.value, form.value)) {
        selectedTab.value = tabId;
    }
};

const nextTab = async () => {
    if (currentIndex.value < tabs.value.length - 1) {
        await changeTab(tabs.value[currentIndex.value + 1].id);
    }
};

const prevTab = async () => {
    if (currentIndex.value > 0) {
        await changeTab(tabs.value[currentIndex.value - 1].id);
    }
};
const canManageNotes = computed(() => {
    return hasPermission("AgencyNotes");
});
const canViewNotes = computed(() => {
    return hasPermission("AgencyNotesView");
});


const schema = computed(() => {
    const shape: Record<string, unknown> = {
        ori: string()
            .required()
            .max(9)
            .label("9-Digit ORI")
            .test("unique-ori", "Ori ${value} already exists", async function (value) {
                if (editMode.value) {
                    return true;
                }
                var oriAlreadyExists = value != undefined ? await agencyStore.checkIfOriExists(value) : true;
                return !oriAlreadyExists;
            }),
        oriName: string().required().max(100).label("Agency Name"),
        countyCode: string().required().label("County"),
        dataCollectionStatus: array().of(
            object({
                vendorCode: string()
                    .nullable()
                    .label("Vendor")
                    .when(["agencyStartDate", "agencyEndDate"], {
                        is: (startDate: string | null, endDate: string | null) => !!startDate || !!endDate,
                        then: (schema) => schema.required(),
                    }),
                agencyEndDate: mixed<string | Date>()
                    .nullable()
                    .test("end-after-start", "End Date must be after Start Date", function (endDate) {
                        const startDate = this.parent.agencyStartDate;
                        if (!startDate || !endDate) {
                            return true;
                        }
                        return new Date(endDate) > new Date(startDate);
                    }),
            })
        ),
    };

    if (settings.value?.ori7.visible) {
        shape.ori7s = string().max(7);
    }

    if (settings.value?.mailingAddress.visible) {
        shape.mailStreet = string().max(160);
        shape.mailTown = string().max(75);
        shape.mailState = string().max(10);
        shape.mailZip = string().max(10);
    }

    if (settings.value?.locationAddress.visible) {
        shape.locStreet = string().max(160);
        shape.locTown = string().max(75);
        shape.locState = string().max(10);
        shape.locZip = string().max(10);
    }

    if (settings.value?.contactsAgencyAdministrator.visible) {
        shape.adminName = string().max(500);
        shape.adminEmail = string().email().max(500).label(`${contactsAgencyAdministratorText.value} Email`);
        shape.adminPhone = string().max(500);
        shape.adminFax = string().max(50);
    }

    if (settings.value?.contactsUcrProgramManager.visible) {
        shape.ucrName = string().max(500);
        shape.ucrEmail = string().email().max(500).label(`${contactsUcrProgramManagerText.value} Email`);
        shape.ucrPhone = string().max(500);
        shape.ucrFax = string().max(50);
    }

    if (settings.value?.contactsCommander.visible) {
        shape.commName = string().max(500);
        shape.commEmail = string().email().max(500).label(`${contactsCommanderText.value} Email`);
        shape.commPhone = string().max(500);
        shape.commFax = string().max(50);
    }

    return object<AgencyItemDetails>(shape as never);
});

const {handleSubmit, errors, resetValidation} = useFormValidation();

const updateNotes = async () => {
    await agencyStore.updateNotes(props.id!, form.value.itemNote);
};

const save = async () => {
    if (await handleSubmit(schema.value, form.value)) {
        if (props.id) {
            await agencyStore.update(props.id!, form.value!);           
        } else {
            await agencyStore.create(form.value!);           
        }
        setTimeout(() => {
            window.location.href = `Agencies.aspx`;
        }, 2000);
    }
};

onMounted(async () => {
    await agencyStore.getSettings();

    await agencyStore.listLookups();

    if (editMode.value) {
        form.value = await agencyStore.getById(props.id!);
        flags.value.sameAs7 = form.value.ori7 == form.value.ori7Fbi;
        flags.value.differentOri9 = !!form.value.ori9Fbi && form.value.ori9Fbi != form.value.ori;
    } else {
        await agencyStore.listDatasets();
        form.value = {} as AgencyItemDetails;
        form.value.agencyTypeCode = -1;
        form.value.toFbi = true;
        form.value.dataCollectionStatus = datasets.value?.map(
            (x) =>
                ({
                    label: x.label,
                    dataSetId: x.code,
                    name: x.name,
                    vendorCode: "UV",
                    agencyEnabled: false,
                } as DataCollectionStatus)
        );
    }

    vendorStore.tableOptions.itemsPerPage = 10000;
    await vendorStore.list();
    await agencyTypeStore.list();
    await countyStore.list();
});
</script>
<style>
.agency-access {
    font-size: 0.9rem;
}
</style>
